import { useCallback, useMemo, useRef, useState } from "react";
import { useMainStore } from "@/stores";
import { useAiPeopleSearch, useContactAvailability } from "@/features/contacts/hooks/useApollo";
import { CONTACT_INFO_FILTER_KEYS, PROSPECT_DEFAULT_FILTERS } from "../constants/prospectConfig";
import { showWarning } from "@/shared/utils/toast";
import { useSavedProspectSearches } from "./useSavedProspectSearches";
import { getPersonCompany, getPersonFullName, linkedinProfileKey, truncateLabel } from "../utils/prospectUtils";
import type { ProspectItem, ProspectPagination, SearchCampaign } from "../types";

interface AiSearchRequest {
  source: "user_query" | "campaign_icp";
  query?: string;
  campaignId?: string | number;
  filters: Record<string, any>;
  exclude?: ProspectItem[];
}

interface AiSearchResult {
  people: ProspectItem[];
  suggestions?: string[];
  applied_filters?: Record<string, any>;
  normalized_query?: string;
  dropped?: Record<string, number>;
}

// The AI backend accepts this many people to skip on "Find more".
const MAX_EXCLUDE = 500;

const droppedCount = (result: AiSearchResult) =>
  Object.values(result.dropped || {}).reduce((sum, n) => sum + (Number(n) || 0), 0);

const hasEmail = (p: ProspectItem) => Boolean(p.email) || p.availability?.email === true;
const hasPhone = (p: ProspectItem) => Boolean(p.phone) || p.availability?.phone === true;

/** "Has Work Email" (on by default) and "Has Phone Number" (off by default) as applied. */
export function contactInfoWanted(filters: Record<string, any>) {
  const effective = { ...PROSPECT_DEFAULT_FILTERS, ...filters };
  return { email: effective.has_work_email !== false, phone: effective.has_phone === true };
}

/** People who pass the contact-info toggles; only confirmed emails / phones count. */
export function passesContactFilters(p: ProspectItem, wanted: { email: boolean; phone: boolean }) {
  return (!wanted.email || hasEmail(p)) && (!wanted.phone || hasPhone(p));
}

/** Has Work Email / Has Phone Number at their defaults: always part of the applied filters, so
 *  the panel shows a toggle that is on (and filtering the results) as applied. */
const CONTACT_TOGGLE_DEFAULTS: Record<string, any> = Object.fromEntries(
  CONTACT_INFO_FILTER_KEYS.map((k) => [k, PROSPECT_DEFAULT_FILTERS[k]]),
);

/** The panel's filters without the contact-info toggles, to tell if a new search is needed. */
const searchCriteria = (filters: Record<string, any>) => {
  const rest: Record<string, any> = { ...PROSPECT_DEFAULT_FILTERS, ...filters };
  CONTACT_INFO_FILTER_KEYS.forEach((key) => delete rest[key]);
  return JSON.stringify(rest, Object.keys(rest).sort());
};

/**
 * Contact search on the Find page. AI web search finds the people (only those whose LinkedIn
 * profile it actually found); ContactOut adds email/phone on import.
 * A query, a campaign's ICP and the filter panel all feed the same search; "Find more"
 * searches again, skipping everyone already shown.
 */
export function useProspectSearch() {
  const userId = useMainStore((state: any) => state.user?.id);

  const [queryText, setQueryText] = useState("");
  const [activeQueryName, setActiveQueryName] = useState("Custom Search");
  const [showResults, setShowResults] = useState(false);
  // Everyone the search found who has an email or a phone (or could not be checked); the
  // table shows the ones passing the contact-info toggles (`prospects` below).
  const [found, setFound] = useState<ProspectItem[]>([]);
  // The query behind the results on screen (a campaign's is written from its ICP); "Find more"
  // and the filter panel re-run it.
  const [lastQuery, setLastQuery] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { savedSearches, saveSearch, deleteSavedSearch } = useSavedProspectSearches();
  // Campaign whose ICP produced the results, so they can be added straight to it.
  const [campaign, setCampaign] = useState<SearchCampaign | null>(null);
  // Filter panel: what the user edits, and what the last search actually applied.
  const [panelFilters, setPanelFilters] = useState<Record<string, any>>({ ...PROSPECT_DEFAULT_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>({ ...CONTACT_TOGGLE_DEFAULTS });

  const { mutateAsync: aiSearch, isPending } = useAiPeopleSearch();
  const { mutateAsync: checkAvailability } = useContactAvailability();
  const [isChecking, setIsChecking] = useState(false);
  // People left out for having no email or phone; "Find more" skips them too.
  const unreachable = useRef<ProspectItem[]>([]);
  // Broader searches the AI offers when a search finds nobody.
  const [suggestions, setSuggestions] = useState<string[]>([]);
  // Bumped by every new search (and New Search); a slower, older response is then ignored
  // instead of replacing what is on screen.
  const searchSeq = useRef(0);
  // True while a search (or Find more) is running on the server. Each one costs real money and
  // keeps running even if the page stops waiting for it, so a second one is not started.
  const inFlight = useRef(false);

  /** Blocks a new search while one is running; says why. */
  const blockIfBusy = useCallback(() => {
    if (!inFlight.current) return false;
    showWarning("A search is already running. Please wait for it to finish.");
    return true;
  }, []);

  // A "Find more" search keeps the table on screen; only a new search shows skeletons.
  const isSearching = (isPending || isChecking) && !isLoadingMore;

  const wanted = useMemo(() => contactInfoWanted(appliedFilters), [appliedFilters]);
  const prospects = useMemo(() => found.filter((p) => passesContactFilters(p, wanted)), [found, wanted]);

  /**
   * Asks which people have a work email or phone (a free check), and leaves out those with
   * neither: they could not be imported anyway. If the check fails, everyone is kept.
   */
  const keepReachable = useCallback(
    async (people: ProspectItem[], checkPhone = false): Promise<{ people: ProspectItem[]; hidden: number }> => {
      if (people.length === 0) return { people, hidden: 0 };
      let byProfile: Record<string, any> = {};
      try {
        const results: Record<string, any> = await (checkAvailability as any)({
          linkedinUrls: people.map((p) => p.linkedin_url).filter(Boolean),
          checkPhone,
        });
        byProfile = Object.fromEntries(Object.entries(results).map(([url, v]) => [linkedinProfileKey(url), v]));
      } catch {
        return { people, hidden: 0 };
      }
      const annotated = people.map((p) => {
        const availability = byProfile[linkedinProfileKey(p.linkedin_url)];
        return availability ? { ...p, availability } : p;
      });
      const isUnreachable = (p: ProspectItem) => p.availability?.email === false && p.availability?.phone === false;
      unreachable.current = unreachable.current.concat(annotated.filter(isUnreachable));
      const reachable = annotated.filter((p) => !isUnreachable(p));
      return { people: reachable, hidden: annotated.length - reachable.length };
    },
    [checkAvailability],
  );

  const callSearch = useCallback(
    async ({ source, query, campaignId, filters, exclude = [] }: AiSearchRequest): Promise<AiSearchResult | null> => {
      try {
        const result: any = await (aiSearch as any)({
          user_id: userId,
          source,
          query,
          campaign_id: campaignId != null ? String(campaignId) : undefined,
          filters,
          exclude: exclude.slice(0, MAX_EXCLUDE).map((p) => ({
            name: getPersonFullName(p),
            company: getPersonCompany(p),
            linkedin_url: p.linkedin_url || "",
          })),
        });
        return { ...result, people: Array.isArray(result?.people) ? result.people : [] };
      } catch {
        // The mutation already toasts the error; the empty state offers "Try again".
        return null;
      }
    },
    [aiSearch, userId],
  );

  /** Runs a new search and shows it; returns the people found (empty on error). */
  const runSearch = useCallback(
    async (request: AiSearchRequest) => {
      if (blockIfBusy()) return [];
      inFlight.current = true;
      const seq = ++searchSeq.current;
      setShowResults(true);
      setFound([]);
      setHasMore(false);
      setAppliedFilters({ ...CONTACT_TOGGLE_DEFAULTS, ...request.filters });
      // Known up front for a typed query, so the saved-search picker shows it while searching.
      setLastQuery(request.source === "user_query" ? request.query || "" : null);
      setSuggestions([]);
      unreachable.current = [];
      setIsChecking(true);
      let result: AiSearchResult | null = null;
      let hidden = 0;
      try {
        result = await callSearch(request);
        if (result && seq === searchSeq.current) {
          const reachable = await keepReachable(result.people, contactInfoWanted(request.filters).phone);
          result = { ...result, people: reachable.people };
          hidden = reachable.hidden;
        }
      } finally {
        inFlight.current = false;
        if (seq === searchSeq.current) setIsChecking(false);
      }
      if (seq !== searchSeq.current) return [];
      if (!result) {
        setLastQuery(null);
        return [];
      }
      setSuggestions(result.suggestions || []);
      const query = request.source === "campaign_icp" ? result.normalized_query || "" : request.query || "";
      setLastQuery(query);
      if (request.source === "campaign_icp") setQueryText(query);
      setFound(result.people);
      setHasMore(result.people.length > 0);
      // The criteria the search used (read from the query, or the user's own) go in the panel;
      // the contact-info toggles stay as the user set them.
      const toggles = Object.fromEntries(
        CONTACT_INFO_FILTER_KEYS.filter((k) => k in request.filters).map((k) => [k, request.filters[k]]),
      );
      const applied = { ...CONTACT_TOGGLE_DEFAULTS, ...(result.applied_filters || request.filters), ...toggles };
      setAppliedFilters(applied);
      setPanelFilters({ ...PROSPECT_DEFAULT_FILTERS, ...applied });

      const want = contactInfoWanted(applied);
      const need = want.email && want.phone ? "email or phone" : want.phone ? "phone number" : "work email";
      const visible = result.people.filter((p) => passesContactFilters(p, want));
      const leftOut = hidden + (result.people.length - visible.length);
      if (visible.length === 0) {
        const dropped = droppedCount(result) + leftOut;
        showWarning(
          dropped > 0
            ? `No contacts with a ${need} yet. ${dropped} ${dropped === 1 ? "person was" : "people were"} left out. Try one of the suggested searches or change the filters.`
            : "No contacts found matching your criteria. Try one of the suggested searches.",
        );
      } else if (leftOut > 0) {
        showWarning(`${leftOut} ${leftOut === 1 ? "person was" : "people were"} left out: no ${need} available.`);
      }
      return visible;
    },
    [callSearch, keepReachable, blockIfBusy],
  );

  const search = useCallback(
    async (query: string) => {
      const trimmed = query.trim();
      if (!trimmed) {
        showWarning("Please enter a query to find contacts.");
        return;
      }
      if (blockIfBusy()) return;
      setQueryText(trimmed);
      setActiveQueryName(truncateLabel(trimmed, 50));
      setCampaign(null);
      // A new query brings its own criteria; the last search's filters would fight it.
      const people = await runSearch({ source: "user_query", query: trimmed, filters: {} });
      // Only a search that found people is worth keeping.
      if (people.length > 0) saveSearch({ query: trimmed, resultCount: people.length });
    },
    [runSearch, saveSearch, blockIfBusy],
  );

  /** Finds contacts that match a campaign's ICP. */
  const searchByCampaign = useCallback(
    async (target: SearchCampaign) => {
      if (!target?.id || blockIfBusy()) return;
      setCampaign(target);
      setQueryText("");
      setActiveQueryName(truncateLabel(`Campaign: ${target.name}`, 50));
      const people = await runSearch({ source: "campaign_icp", campaignId: target.id, filters: {} });
      if (people.length > 0) saveSearch({ campaignId: target.id, campaignName: target.name, resultCount: people.length });
    },
    [runSearch, saveSearch, blockIfBusy],
  );

  /**
   * Applies the filter panel. If only "Has Work Email" / "Has Phone Number" changed, the people
   * already found are filtered again (checking phones first if needed); anything else re-runs
   * the search.
   */
  const applyPanelFilters = useCallback(async () => {
    if (found.length > 0 && searchCriteria(panelFilters) === searchCriteria(appliedFilters)) {
      const next = {
        ...appliedFilters,
        ...Object.fromEntries(CONTACT_INFO_FILTER_KEYS.map((k) => [k, panelFilters[k]])),
      };
      const unchecked = found.filter((p) => p.availability && p.availability.phone == null);
      if (contactInfoWanted(next).phone && unchecked.length > 0) {
        const seq = searchSeq.current;
        setIsChecking(true);
        try {
          const { people } = await keepReachable(unchecked, true);
          if (seq !== searchSeq.current) return;
          const byId = new Map(people.map((p) => [String(p.id), p]));
          setFound((prev) => prev.map((p) => byId.get(String(p.id)) || p));
        } finally {
          if (seq === searchSeq.current) setIsChecking(false);
        }
      }
      setAppliedFilters(next);
      return;
    }
    const query = (lastQuery ?? queryText).trim();
    await runSearch({ source: "user_query", query, filters: { ...panelFilters } });
  }, [found, panelFilters, appliedFilters, keepReachable, lastQuery, queryText, runSearch]);

  const clearPanelFilters = useCallback(() => {
    setPanelFilters({ ...PROSPECT_DEFAULT_FILTERS });
    // Results stay filtered by the default toggles, so those still show as applied.
    setAppliedFilters({ ...CONTACT_TOGGLE_DEFAULTS });
  }, []);

  /** Searches again for people not already shown; new ones go on top. */
  const findMore = useCallback(async () => {
    if (lastQuery == null || isLoadingMore || blockIfBusy()) return;
    const seq = searchSeq.current;
    inFlight.current = true;
    setIsLoadingMore(true);
    try {
      const result = await callSearch({
        source: "user_query",
        query: lastQuery,
        filters: appliedFilters,
        exclude: found.concat(unreachable.current),
      });
      if (!result || seq !== searchSeq.current) return;
      const shown = new Set(found.map((p) => String(p.id)));
      const { people: fresh } = await keepReachable(
        result.people.filter((p) => !shown.has(String(p.id))),
        wanted.phone,
      );
      if (seq !== searchSeq.current) return;
      setFound((prev) => fresh.concat(prev));
      if (fresh.length === 0) setHasMore(false);
      if (!fresh.some((p) => passesContactFilters(p, wanted))) {
        showWarning("No more contacts matching your filters for this search.");
      }
    } finally {
      inFlight.current = false;
      setIsLoadingMore(false);
    }
  }, [lastQuery, isLoadingMore, callSearch, keepReachable, appliedFilters, found, wanted, blockIfBusy]);

  /** Runs the search on screen again: its campaign, its query, or its filters. */
  const retry = useCallback(() => {
    if (campaign) return searchByCampaign(campaign);
    if (queryText.trim()) return search(queryText);
    return applyPanelFilters();
  }, [campaign, queryText, search, searchByCampaign, applyPanelFilters]);

  const startNewSearch = useCallback(() => {
    searchSeq.current += 1;
    // The abandoned search no longer owns the "checking" state.
    setIsChecking(false);
    setSuggestions([]);
    setShowResults(false);
    setQueryText("");
    setFound([]);
    setLastQuery(null);
    setHasMore(false);
    setCampaign(null);
  }, []);

  // AI search has no pages; kept for the results header's shape.
  const pagination: ProspectPagination = { page: 1, total_pages: 1, total_entries: prospects.length };

  return {
    suggestions,
    lastQuery,
    queryText,
    setQueryText,
    activeQueryName,
    showResults,
    prospects,
    pagination,
    savedSearches,
    campaign,
    panelFilters,
    setPanelFilters,
    appliedFilters,
    isSearching,
    isLoadingMore,
    hasMore,
    search,
    searchByCampaign,
    applyPanelFilters,
    clearPanelFilters,
    findMore,
    retry,
    startNewSearch,
    deleteSavedSearch,
  };
}
